import Roles from './constants/Roles'
import hasFeature from './featureFlags'

if (!window.global) {
  ;(window.global as any) = window
}

const { user, orgConfig, current } = window
const PROD = (import.meta.env.MODE || '').toLowerCase().includes('prod')
const DEV = (import.meta.env.MODE || '').toLowerCase().includes('dev')
const STAGING = import.meta.env.VITE_STAGING === '1' || import.meta.env.VITE_STAGING === 'true'
const TEST = (import.meta.env.MODE || '') === 'test'
const ROLES = ['admin', 'user', 'reports_only', 'store', 'store_admin']

const CABLE_URL = `ws${PROD ? 's' : ''}://${window.location.host}/cable`

const OFFER_UP_SHIPPING = () => hasFeature('offer-up-shipping')
const PRODUCTS_V4 = () => true // you can remove this JS flag
const PRODUCTS_V5 = () => hasFeature('products-v5')
const EDIT_LISTING_V2 = () => PRODUCTS_V5()
const FACEBOOK = () => PRODUCTS_V5()
const SHIPPING = () => [Roles.MANAGER.id, Roles.ADMIN.id, Roles.SHIPPING.id].includes(user.role)
const PRODUCT_MANAGER = () => true // you can remove this JS flag
const SGW_OUTAGE = () => hasFeature('sgw-outage')
const INCIDENT_IN_PROGRESS_STRING_FLAG = () => hasFeature('incident-in-progress')
const EASYPOST_OUTAGE = () => hasFeature('easy-post-outage')
const FEDEX_OUTAGE = () => hasFeature('fedex-outage')
const AF_OUTAGE = () => hasFeature('authenticate-first-outage')

const DISABLE_MANIFEST_PRODUCT_TITLES_FOR_PRINTING = () => hasFeature('disable-manifest-product-titles-for-printing')
const PRODUCT_TAGS = () => hasFeature('product-tags')
const BLUEBOX_FULFILLER = () => hasFeature('bluebox-fulfiller')
const PACKSIZE_ORG = () => hasFeature('packsize-organization')
const CURSOR_PAGINATED_LISTING_MANAGER = () => hasFeature('cursor-based-pagination-lm')
const PICK_LISTS = () => hasFeature('pick-lists')
const PACK_MODE = () => hasFeature('pack-mode')
const PICKING_PROFILES = () => hasFeature('picking-profiles')
const CARRIER_INSURANCE = () => hasFeature('carrier-insurance')
const UPRIGHT_LOCAL = () => hasFeature('upright-local')
const IMAGE_EDITOR = () => hasFeature('image-editor')
const NEW_RBAC = () => hasFeature('new-rbac')
const BARCODE_BY_ID = () => hasFeature('barcode-locations-by-id')
const MANIFEST_TRACKING = () => hasFeature('manifest-tracking')
const EBAY_MULTI_ACCOUNT = () => hasFeature('ebay-multi-account')
const SQS_FEED = () => hasFeature('sqs-feed')
const SHOPIFY_VARIANTS = () => hasFeature('shopify-variants')
const SHIPSURANCE = () => hasFeature('shipsurance')
const EBAY_AUTOFILL_SPECIFICS = () => hasFeature('ebay-autofill-specifics')
const NEW_TAB_PDF_PRINTER = () => hasFeature('new-tab-pdf-printer')
const ALLOW_CONCURRENT_OVERRIDE = () => hasFeature('allow-concurrent-override')
const SHOPIFY_PRODUCT_TYPE_INCLUDES_PARENTS = () => hasFeature('shopify-product-type-includes-parents')
const SHOPIFY_VENDOR_DROPDOWN = () => hasFeature('shopify-vendor-dropdown')
const SHOPIFY_OAUTH_CONNECT = () => hasFeature('shopify-oauth-connect')
const LISTER_BOOKS = () => hasFeature('lister-books')
const RELOAD_FRONTEND_ON_UPDATE = () => hasFeature('reload-frontend-on-update')
const LISTING_REVIEW = () => hasFeature('listing-review')
const HAMMOQ_IMPORT_BUTTON = () => hasFeature('hammoq-import-button')
const LISTING_STRATEGY_IMPROVEMENTS = () => hasFeature('listing-strategy-improvements')
const RETAIL_LOCATIONS = () => hasFeature('retail-locations')
const DISALLOW_SGW_ASSET_IMPORT = () => hasFeature('disallow-sgw-assets-import')
const CONFIRM_IMAGE_REMOVAL = () => hasFeature('confirm-image-removal')
const PRICE_OVERRIDES = () => hasFeature('price-overrides')
const CHANNEL_SGW_ID = 1 // can't import client/constants/Channels.ts without creating a dependency cycle
const EXPRESS_LIST = () =>
  hasFeature('express-list') && !!current.organization.uniqueConnectedMarkets?.find(cm => cm.id === CHANNEL_SGW_ID)

const GWF_INTEGRATION = () => hasFeature('goodwill-finds-integration')
const EMBEDDED_LISTINGS_LOCATION_HUB = () => hasFeature('embedded-listings-location-hub')
const ETSY_INTEGRATION = () => hasFeature('etsy')
const AMAZON_INTEGRATION = () => hasFeature('amazon-integration')

const ANNOUNCEMENT_BANNER = () => hasFeature('announcement-banner')

const COALESCE_SPECIFICS = () => hasFeature('coalesce-specifics')

const CLOTHING_CONVEYOR = () => hasFeature('clothing-conveyor')

const LOGIN_SECURITY = () => hasFeature('login-security')

const MARKETS = {
  shopgoodwill: 1,
  ebay: 2,
  offerUp: 3,
  facebook: 7,
  bluebox: 8,
  shopify: 9,
  goodwillfinds: 10,
  etsy: 11,
  amazon: 12
}

if (!OFFER_UP_SHIPPING()) orgConfig.markets = orgConfig.markets.filter(m => m.id !== MARKETS.offerUp)

const CARRIERS = {
  Pickup: 1,
  FedEx: 2,
  UPS: 3,
  USPS: 4
}

const MAGIC_NUMBER = 2019561246

const FACEBOOK_APP_ID = '236770127025028'

const generateMarketsMap = () => {
  try {
    // orgConfig.markets, see Organization.connected_markets
    return orgConfig.markets.reduce((acc, market) => {
      acc[market.id] = market
      return acc
    }, {})
  } catch (e) {
    return e
  }
}

// All markets, including non-listable markets like Bluebox.
// The values come from Organization.connected_markets
const MARKETS_MAP = generateMarketsMap()

if (BLUEBOX_FULFILLER()) {
  MARKETS_MAP[MARKETS.bluebox] = {
    id: MARKETS.bluebox,
    // Hardcoding this for org 278 (1554) and org 609 (1389)
    // it is needed throughout the app for BLUEBOX fulfillers
    // of which there are only two
    market_account_id: current.organization.id === 278 ? 1554 : 1389,
    name: 'bluebox',
    human_readable_name: 'Bluebox',
    onboarded: true
  }
}

// Markets that can be Listed too.
const LISTABLE_MARKETS_MAP = generateMarketsMap()

const MARKET_URLS = {
  shopgoodwill: STAGING
    ? 'https://sellers.sgwsandbox.com/sellers/tools/zoomItem.asp?ItemID='
    : 'https://www.shopgoodwill.com/item/',
  offer_up: STAGING ? 'https://offerup-stg.com/item/detail/' : 'https://offerup.com/item/detail/',
  ebay: STAGING ? 'http://cgi.sandbox.ebay.com/itm/' : 'http://ebay.com/itm/',
  facebook: 'https://facebook.com/',
  goodwillfinds: 'https://goodwillfinds.com/',
  etsy: 'https://www.etsy.com/listing/',
  amazon: 'https://www.amazon.com/dp/'
}

const IS_IPAD = navigator.userAgent.match(/iPad/i) != null

export default {
  PROD,
  DEV,
  STAGING,
  TEST,
  FACEBOOK,
  ROLES,
  user,
  orgConfig,
  PRODUCTS_V4,
  PRODUCTS_V5,
  EDIT_LISTING_V2,
  ORGANIZATION_MARKETS: orgConfig.markets,
  MARKETS,
  CARRIERS,
  MARKETS_MAP,
  MARKET_URLS,
  FACEBOOK_APP_ID,
  IS_IPAD,
  MAX_IMAGES: 12,
  DRAFT_PRODUCT_MAX_IMAGES: 25,
  INTERCOM_ENABLED: orgConfig.organization_settings.intercom_enabled,
  LABEL_PRINT_TYPE: orgConfig.organization_settings.label_print_type,
  MAGIC_NUMBER,
  PRODUCT_MANAGER,
  OFFER_UP_SHIPPING,
  CABLE_URL,
  SHIPPING,
  current,
  SGW_OUTAGE,
  EASYPOST_OUTAGE,
  FEDEX_OUTAGE,
  AF_OUTAGE,
  DISABLE_MANIFEST_PRODUCT_TITLES_FOR_PRINTING,
  PRODUCT_TAGS,
  BLUEBOX_FULFILLER,
  LISTABLE_MARKETS_MAP,
  PACKSIZE_ORG,
  CURSOR_PAGINATED_LISTING_MANAGER,
  PICK_LISTS,
  PACK_MODE,
  PICKING_PROFILES,
  CARRIER_INSURANCE,
  UPRIGHT_LOCAL,
  IMAGE_EDITOR,
  NEW_RBAC,
  INCIDENT_IN_PROGRESS_STRING_FLAG,
  BARCODE_BY_ID,
  MANIFEST_TRACKING,
  EBAY_MULTI_ACCOUNT,
  FLAG_DO_NOT_SEND_MANIFEST_ITEMS: orgConfig.flag_do_not_send_manifest_items,
  SQS_FEED,
  SHOPIFY_VARIANTS,
  SHIPSURANCE,
  EBAY_AUTOFILL_SPECIFICS,
  NEW_TAB_PDF_PRINTER,
  ALLOW_CONCURRENT_OVERRIDE,
  SHOPIFY_PRODUCT_TYPE_INCLUDES_PARENTS,
  SHOPIFY_VENDOR_DROPDOWN,
  SHOPIFY_OAUTH_CONNECT,
  LISTER_BOOKS,
  RELOAD_FRONTEND_ON_UPDATE,
  LISTING_REVIEW,
  HAMMOQ_IMPORT_BUTTON,
  LISTING_STRATEGY_IMPROVEMENTS,
  RETAIL_LOCATIONS,
  DISALLOW_SGW_ASSET_IMPORT,
  CONFIRM_IMAGE_REMOVAL,
  PRICE_OVERRIDES,
  GWF_INTEGRATION,
  ANNOUNCEMENT_BANNER,
  EXPRESS_LIST,
  EMBEDDED_LISTINGS_LOCATION_HUB,
  COALESCE_SPECIFICS,
  ETSY_INTEGRATION,
  CLOTHING_CONVEYOR,
  AMAZON_INTEGRATION,
  LOGIN_SECURITY
}
